import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import AdminTopRight from './AdminTopRight'

type MetaResp = { ok:boolean; sessionId:string; exam:string; extension?:string; questions:{id:string; imageUrl?:string|null}[] }
type Ev = { ts:number; type:string; index?:number; value?:string; id?:string; answer?:string; timeMs?:number; pasted?:boolean; x?:number; y?:number; reason?:string }
const TERMINATION_TYPES = new Set(['terminated', 'strike'])
type Me = { discordId:string; tag?:string|null; isAdmin:boolean } | null

export default function ReplayPage() {
  const { sessionId = '' } = useParams()

  // --- Dostęp (opcjonalnie) ---
  const [me, setMe] = useState<Me>(null)
  const [authChecked, setAuthChecked] = useState(false)
  useEffect(() => { (async()=>{
    try { const r = await fetch('/api/me'); const d = await r.json(); setMe(d.me || null) } catch {}
    setAuthChecked(true)
  })() }, [])

  // --- Meta & log ---
  const [meta, setMeta] = useState<MetaResp|null>(null)
  const [log, setLog] = useState<Ev[]>([])
  const [error, setError] = useState<string>('')

  // Refy do logiki odtwarzania (niezależne od re-renderów)
  const logRef = useRef<Ev[]>([])
  const speedRef = useRef<number>(1)
  const posRef = useRef<number>(0)          // numer następnego eventu do zastosowania
  const playheadMsRef = useRef<number>(0)   // czas odtwarzania (ms od t0)
  const playingRef = useRef<boolean>(false)
  const rafRef = useRef<number | null>(null)
  const lastFrameRef = useRef<number>(0)
  const t0Ref = useRef<number>(0)
  const tEndRef = useRef<number>(0)

  // Stany do UI
  const [pos, setPos] = useState(0)
  const [curMs, setCurMs] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [typing, setTyping] = useState('')
  const [qIndex, setQIndex] = useState(0)
  const [cursor, setCursor] = useState<{x:number,y:number}|null>(null)
  const [termination, setTermination] = useState<string>('')

  // sync refów ze stanami sterującymi
  useEffect(()=>{ speedRef.current = speed }, [speed])
  useEffect(()=>{ playingRef.current = playing }, [playing])
  useEffect(()=>{ logRef.current = log }, [log])

  // Wczytaj meta + log
  useEffect(() => {
    (async()=>{
      try {
        setError('')
        setMeta(null)
        setLog([])
        stopInternal() // zatrzymaj ewentualną pętlę

        const m = await fetch(`/api/live-meta/${sessionId}`)
        if (!m.ok) { setError('Brak sesji albo brak uprawnień'); return }
        const metaJson: MetaResp = await m.json()
        setMeta(metaJson)

        const l = await fetch(`/api/live-log/${sessionId}?limit=20000`)
        if (!l.ok) { setError('Brak logu lub brak uprawnień'); return }
        const lj = await l.json()
        const events: Ev[] = Array.isArray(lj.events) ? lj.events.slice() : []
        events.sort((a,b)=>(a.ts||0)-(b.ts||0))
        setLog(events)

        if (events.length) {
          t0Ref.current = events[0]?.ts ?? 0
          tEndRef.current = events[events.length-1]?.ts ?? 0
          resetState()
          posRef.current = 0
          playheadMsRef.current = 0
          setPos(0)
          setCurMs(0)
        } else {
          t0Ref.current = 0
          tEndRef.current = 0
          resetState()
          posRef.current = 0
          playheadMsRef.current = 0
          setPos(0)
          setCurMs(0)
        }
      } catch (e:any) {
        setError(e.message || String(e))
      }
    })()
    return () => stopInternal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  function resetState() {
    setTyping(''); setQIndex(0); setCursor(null); setTermination('')
  }
  function apply(ev: Ev) {
    if (!ev) return
    if (ev.type === 'question' && typeof ev.index==='number') { setQIndex(ev.index); setTyping('') }
    if (ev.type === 'typing') setTyping(ev.value || '')
    if (ev.type === 'cursor' && typeof ev.x==='number' && typeof ev.y==='number') setCursor({x:ev.x,y:ev.y})
    if (ev.type === 'answered') setTyping('')
    if (ev.type === 'terminated') setTermination(ev.reason || 'Przerwano egzamin')
  }

  // --- Odtwarzanie oparte o refy (bez "stale closures") ---
  function loop(now:number) {
    if (!playingRef.current) return
    if (!lastFrameRef.current) lastFrameRef.current = now
    const dt = now - lastFrameRef.current
    lastFrameRef.current = now

    // zaktualizuj playhead
    const speed = speedRef.current || 1
    const durationMs = Math.max(0, tEndRef.current - t0Ref.current)
    playheadMsRef.current = Math.min(durationMs, playheadMsRef.current + dt * speed)

    // zastosuj eventy do bieżącego czasu
    const targetTs = (t0Ref.current || 0) + playheadMsRef.current
    const evs = logRef.current
    //@ts-ignore
    while (posRef.current < evs.length && (evs[posRef.current].ts <= targetTs)) {
      // @ts-ignore
      apply(evs[posRef.current])
      posRef.current++
    }

    // aktualizacje UI (lekko odchudzone)
    setPos(posRef.current)
    setCurMs(playheadMsRef.current)

    if (playheadMsRef.current >= durationMs) {
      // koniec
      setPlaying(false)
      playingRef.current = false
      rafRef.current && cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      return
    }
    rafRef.current = requestAnimationFrame(loop)
  }

  function playInternal() {
    if (playingRef.current) return
    setPlaying(true); playingRef.current = true
    lastFrameRef.current = 0
    rafRef.current = requestAnimationFrame(loop)
  }
  function pauseInternal() {
    setPlaying(false); playingRef.current = false
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
  }
  function stopInternal() {
    pauseInternal()
    resetState()
    posRef.current = 0
    playheadMsRef.current = 0
    setPos(0); setCurMs(0)
  }

  // --- Seek / scrub ---
  function rebuildToIndex(idx:number) {
    idx = Math.max(0, Math.min((logRef.current.length-1), idx))
    resetState()
    for (let i=0;i<=idx;i++) {
      const ev = logRef.current[i]
      if (ev) apply(ev)
    }
    posRef.current = idx+1
    setPos(idx+1)
    const base = t0Ref.current || 0
    const t = logRef.current[idx]?.ts ?? base
    playheadMsRef.current = Math.max(0, t - base)
    setCurMs(playheadMsRef.current)
  }
  function seekToTime(ms:number) {
    const evs = logRef.current
    const durationMs = Math.max(0, tEndRef.current - t0Ref.current)
    ms = Math.max(0, Math.min(durationMs, ms))
    if (!evs.length) {
      playheadMsRef.current = ms
      setCurMs(ms)
      return
    }
    const tgt = (t0Ref.current || 0) + ms
    // binsearch po ts -> ostatni <= tgt
    let lo=0, hi=evs.length-1, ans=0
    while (lo<=hi) {
      const mid=(lo+hi)>>1
      if (evs[mid] && evs[mid].ts <= tgt) { ans=mid; lo=mid+1 } else hi=mid-1
    }
    rebuildToIndex(ans)
  }

  // Krok po zdarzeniach
  function stepForward(n=1) { pauseInternal(); rebuildToIndex(Math.min(logRef.current.length-1, (posRef.current + n - 1))) }
  function stepBack(n=1)    { pauseInternal(); rebuildToIndex(Math.max(0, (posRef.current - n - 1))) }

  // Skoki po pytaniach
  const questionEventIndexByQ = useMemo(()=>{
    const map = new Map<number, number>()
    log.forEach((ev, i) => {
      if (ev.type === 'question' && typeof ev.index === 'number' && !map.has(ev.index)) map.set(ev.index, i)
    })
    return map
  }, [log])

  function jumpToQuestion(i:number) {
    if (!questionEventIndexByQ.has(i)) return
    pauseInternal()
    const idx = questionEventIndexByQ.get(i)!
    rebuildToIndex(idx)
  }
  function prevQuestion(){ jumpToQuestion(Math.max(0, qIndex-1)) }
  function nextQuestion(){ jumpToQuestion(Math.min((meta?.questions.length||1)-1, qIndex+1)) }

  // Klawisze
  useEffect(()=>{
    function onKey(e:KeyboardEvent){
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.code==='Space'){ e.preventDefault(); playingRef.current ? pauseInternal() : playInternal() }
      if (e.code==='ArrowRight'){ e.preventDefault(); stepForward(e.shiftKey?10:1) }
      if (e.code==='ArrowLeft'){ e.preventDefault(); stepBack(e.shiftKey?10:1) }
      if (e.code==='BracketRight'){ e.preventDefault(); setSpeed(s=>Math.min(4, +(s+0.25).toFixed(2))) }
      if (e.code==='BracketLeft'){ e.preventDefault(); setSpeed(s=>Math.max(0.25, +(s-0.25).toFixed(2))) }
      if (e.code==='PageDown'){ e.preventDefault(); nextQuestion() }
      if (e.code==='PageUp'){ e.preventDefault(); prevQuestion() }
      if (e.code==='Home'){ e.preventDefault(); stopInternal() }
      if (e.code==='End'){ e.preventDefault(); pauseInternal(); seekToTime(Math.max(0, tEndRef.current - t0Ref.current)) }
    }
    window.addEventListener('keydown', onKey)
    return ()=>window.removeEventListener('keydown', onKey)
  }, [qIndex, meta?.questions.length])

  // Wyliczenia do UI
  const totalQ = meta?.questions.length ?? 0
  const durationMs = Math.max(0, tEndRef.current - t0Ref.current)
  const timeProgressPct = durationMs ? Math.min(100, Math.max(0, (curMs / durationMs) * 100)) : 0
  const percentQuestions = useMemo(()=> totalQ>1 ? Math.round((qIndex/(totalQ-1))*100) : 0, [qIndex, totalQ])
  const imgSrc = useMemo(()=>{
    if (!meta) return ''
    const q = meta.questions[qIndex]; if (!q) return ''
    return q.imageUrl || `/assets/${meta.exam}/${q.id}.${meta.extension || 'jpg'}`
  }, [meta, qIndex])

  return (
    <Shell>
      <AdminTopRight />

      {error && <div className="card" style={{marginBottom:12}}><b>Błąd:</b> <span className="muted">{error}</span></div>}

      {!!cursor && (
        <div className="cursor-global" style={{ left: `${(cursor.x||0)*100}vw`, top: `${(cursor.y||0)*100}vh` }}/>
      )}

      <div className="top">
        <div className="title"><h2>Odtwarzanie – {meta?.exam || ''}</h2><span className="badge">{sessionId.slice(0,8)}…</span></div>
        <div className="muted">{Math.min(qIndex+1,totalQ)} / {totalQ}</div>
      </div>

      {/* postęp czasu */}
      <div className="progress"><div className="bar" style={{ width: `${timeProgressPct}%` }} /></div>

      <div className="card">
        <div className="cols">
          <div className="left">
            <div className="imageWrap">{imgSrc ? <img src={imgSrc} alt="" /> : <div className="ph">Brak obrazu</div>}</div>
            <div className="muted" style={{marginTop:8}}>Pytanie {qIndex+1}/{totalQ} · Postęp {percentQuestions}%</div>
            {termination && <div className="alert">{termination}</div>}
          </div>

          <div className="right">
            <label>Wpis kandydata (odtwarzanie)</label>
            <div className="inputGhost">{typing || <span className="muted">…</span>}</div>

            <div className="controls">
              <button onClick={playingRef.current ? pauseInternal : playInternal}>{playingRef.current ? 'Pauza' : 'Odtwarzaj'}</button>
              <button onClick={stopInternal}>Stop</button>
              <button onClick={()=>stepBack(1)}>◀ Krok</button>
              <button onClick={()=>stepForward(1)}>Krok ▶</button>
              <label className="sp">Prędkość
                <select value={speed} onChange={e=>setSpeed(Number(e.target.value))}>
                  <option value={0.25}>0.25x</option>
                  <option value={0.5}>0.5x</option>
                  <option value={1}>1x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                  <option value={3}>3x</option>
                  <option value={4}>4x</option>
                </select>
              </label>
            </div>

            {/* scrubber po czasie */}
            <input
              type="range"
              min={0}
              max={Math.max(0, durationMs)}
              step={50}
              value={curMs}
              onChange={e=>{ const v = Number(e.target.value); pauseInternal(); seekToTime(v) }}
            />

            {/* skoki po pytaniach */}
            <div className="qnav">
              <button onClick={prevQuestion} disabled={qIndex<=0}>← Poprzednie pytanie</button>
              <select value={qIndex} onChange={e=>jumpToQuestion(Number(e.target.value))}>
                {Array.from({length: totalQ}).map((_,i)=>(<option key={i} value={i}>Skocz do pytania #{i+1}</option>))}
              </select>
              <button onClick={nextQuestion} disabled={qIndex>=totalQ-1}>Następne pytanie →</button>
            </div>
          </div>
        </div>
      </div>

      <style>{css}</style>
    </Shell>
  )
}

function Shell({children}:{children:React.ReactNode}) {
  return (<div className="wrap"><style>{css}</style>{children}</div>)
}

const css = `
:root{--bg:#0b0d12;--card:#111525;--card2:#0e1220;--border:#1f2430;--text:#e8e8ec;--muted:#a6a8ad;--acc:#7a2cff;--ok:#2e9c4b}
*{box-sizing:border-box} body{background:linear-gradient(180deg,#0b0d12,#0a0c14);color:var(--text);font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial;margin:0}
.wrap{max-width:1100px;margin:0 auto;padding:24px}
.top{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
.title{display:flex;gap:10px;align-items:center}.badge{padding:4px 10px;background:#1b1f2d;border:1px solid #2a3042;border-radius:999px;color:#a6a8ad;font-size:12px}
.muted{color:#a6a8ad}
.progress{height:8px;background:#151a27;border:1px solid #20263a;border-radius:999px;overflow:hidden;margin:8px 0 18px}
.bar{height:8px;background:#7a2cff;width:0%}
.card{background:radial-gradient(60% 120% at 0 0,rgba(122,44,255,.12),transparent),var(--card);border:1px solid var(--border);border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,.45);padding:16px}

.cols{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.imageWrap{position:relative;min-height:360px;border-radius:14px;overflow:hidden;border:1px solid #262c3f;background:var(--card2)}
.imageWrap img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.inputGhost{min-height:44px;padding:10px 12px;border-radius:12px;border:1px solid #2b3242;background:#0e1220;color:#fff;font-size:16px}
.alert{margin-top:10px;padding:10px 12px;border-radius:10px;border:1px solid #40212b;background:rgba(255,107,107,.1);color:#ffb0b0;font-weight:600}

.controls{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:12px 0}
.controls button{padding:10px 14px;border-radius:10px;border:0;background:#7a2cff;color:#fff;font-weight:700;cursor:pointer}
.controls button:disabled{opacity:.6;cursor:not-allowed}
.sp{display:flex;align-items:center;gap:6px}
input[type=range]{width:100%}

.qnav{display:flex;gap:10px;align-items:center;margin-top:12px;flex-wrap:wrap}
.qnav button{padding:8px 12px;border-radius:10px;border:0;background:#1b2440;color:#e8e8ec}
.qnav select{background:#0e1220;border:1px solid #2b3242;color:#fff;border-radius:8px;padding:8px}

/* globalny kursor */
.cursor-global{position:fixed;width:22px;height:22px;border-radius:999px;transform:translate(-50%,-50%);
  background:rgba(255,255,255,.14);border:2px solid rgba(255,255,255,.9);box-shadow:0 0 0 6px rgba(122,44,255,.25);
  pointer-events:none;transition:left .05s linear, top .05s linear;z-index:1000}
`
